<template>
    <div>
        <form :id="formId" @submit.prevent="processForm" style="max-width:375px;" class="mx-auto">
            <input-group v-if="singleLine" class="full-focus rounded-pill">
                <form-input name="phone" type="tel" :placeholder="placeholder" class="bg-body rounded-start-pill border-end-0 text-center ps-5" v-model="localNumber" @input="validateForm"/>
                <ui-button :submit="true" :outline="true" color="secondary" class="bg-body border-start-0 d-flex align-items-center" :disabled="buttonsLoading" aria-label="Submit">
                    <right-arrow-icon :size="16" />
                </ui-button>
            </input-group>
            <template v-else>
                <form-group :valid="localNumberValid">
                    <form-input name="phone" type="tel" :placeholder="placeholder" class="rounded-pill text-center bg-body" v-model="localNumber" @input="validateForm"/>
                </form-group>
                <form-group class="text-center">
                    <ui-button color="secondary" :submit="true" :loading="buttonsLoading" :disabled="buttonsLoading">
                        Submit
                    </ui-button>
                </form-group>
            </template>

            <bot-challenge v-if="showChallenge" @verify="verifyHuman" @close="showChallenge=false" />
        </form>

        <modal v-if="showEmailModal" :open="emailModalOpen" @close="closeEmailModal">
            <template v-if="showThankYou" #header>
                Thank You!
            </template>
            <template v-else #header>
                Questions?
            </template>
            <template v-if="showThankYou" #default>
                <p>We will reach out shortly</p>
            </template>
            <template v-else #default>
                <p v-if="showAddress">
                    Please provide the information below to start a conversation:
                </p>
                <p v-else>
                    Please provide the information below to start a conversation with a representative.
                </p>
                <form id="phoneCtaFollowup" @submit.prevent="processFollowUpForm">

                    <form-group :valid="firstNameValid">
                        <form-label>First Name:</form-label>
                        <form-input name="first-name" v-model="firstName" @input="validateFollowUp"/>
                    </form-group>
                    
                    <form-group :valid="lastNameValid">
                        <form-label>Last Name:</form-label>
                        <form-input name="last-name" v-model="lastName" @input="validateFollowUp"/>
                    </form-group>
                    
                    <form-group :valid="emailValid">
                        <form-label>Email:</form-label>
                        <form-input name="email" type="email" v-model="email" @input="validateFollowUp"/>
                    </form-group>

                    <form-address v-if="showAddress" v-model="address" :valid="addressValid" @input="validateFollowUp" :shortState="true" autocomplete="shipping"/>

                    <form-group v-if="showHasBooking" :valid="hasBookingValid">
                        <form-label>Do you have an existing reservation?</form-label>
                        <form-select :options="hasBookingOptions" v-model="hasBooking" @input="validateFollowUp" />
                    </form-group>
                </form>
            </template>
            <template v-if="showThankYou" #buttons>
                <ui-button color="secondary" @click="emailModalOpen=false" :underline="true" block="sm">
                    Close
                </ui-button>
            </template>
            <template v-else #buttons>
                <ui-button color="secondary" @click="emailModalOpen=false" :underline="true" :loading="buttonsLoading" :disabled="buttonsLoading" class="order-2 order-md-1" block="sm">
                    Close
                </ui-button>
                <ui-button :submit="true" form="phoneCtaFollowup" :loading="buttonsLoading" :disabled="buttonsLoading" class="order-1 order-md-2 mb-3 mb-md-0" block="sm">
                    Submit
                </ui-button>
            </template>
        </modal>
    </div>
</template>

<script type="text/javascript">
    import BotChallenge from '@/components/Content/Auth/BotsChallenge.vue'; 
    import RightArrowIcon from '@/assets/icons/right-arrow.svg.vue';
    import CommonComponents from '@/components/Common.vue';
    import { UseUIStore } from '@/store/UI.js';

    import Api from '@/classes/Api.js';
    import FormOption from '@/classes/FormOption.js';
    import ReCaptcha from '@/classes/Google/ReCaptcha.js';
    import Analytics from '@/classes/Analytics.js';

    export default {
        components: {
            BotChallenge,
            RightArrowIcon,
            ...CommonComponents,
        },

        setup() {
            return {
                uiStore: UseUIStore(),
            };
        },

        data() {
            return {
                buttonsLoading: false,

                showChallenge: false,
                botKey: null,
                botVerified: false,

                hasBookingOptions: null,

                localNumber: null,
                startValidation: false,
                localNumberValid: null,

                showEmailModal: false,
                emailModalOpen: true,

                firstName: null,
                lastName: null,
                email: null,
                address: null,
                hasBooking: null,

                startValidation2: false,
                firstNameValid: null,
                lastNameValid: null,
                emailValid: null,
                addressValid: null,
                hasBookingValid: null,
                ctaId: null,
                ctaToken: null,

                showThankYou: false,
            }
        },

        // submit event will include the id and token
        emits: ['submit', 'followup'],

        props: {
            // the CTA type
            type: {
                type: String,
                default: 'guests',
            },

            // used the set the form id for hubspot
            id: {
                type: String,
            },

            placeholder: {
                type: String,
                default: 'Enter phone # to connect now',
            },

            singleLine: {
                type: Boolean,
                default: false,
            },

            pageLocation: {
                type: String,
            },

            // if true will show a follow up email modal
            followUp: {
                type: Boolean,
                default: true,
            },
        },

        created() {
            this.hasBookingOptions = [
                new FormOption(true, 'Yes'),
                new FormOption(false, 'Not Yet'),
            ];
        },

        computed: {
            formId() {
                if (this.id) {
                    return this.id;
                }

                return 'cta-form-'+(this.type || 'general')+'-'+(this.pageLocation || '');
            },

            showAddress() {
                if ((this.type == 'rent') || (this.type == 'mangement')) {
                    return true;
                }
                return false;
            },

            showHasBooking() {
                if (this.type == 'concierge') {
                    return true;
                }
                return false;
            },
        },

        methods: {
            validateForm() {
                if (!this.startValidation) return;

                let valid = true;
                this.localNumberValid = null;
                
                if ((!this.localNumber) || (this.localNumber.length < 5)) {
                    valid = false;
                    this.localNumberValid = false;
                }

                return valid;
            },

            async processForm() {
                this.startValidation = true;
                if (!this.validateForm()) {
                    this.uiStore.toast({
                        error: true,
                        title: 'Number Required',
                        message: 'Please enter a valid phone number',
                    });

                    return;
                }

                this.buttonsLoading = true;

                let token = await ReCaptcha.submit();

                let data = {
                    phone: this.localNumber,
                    type: this.type,
                    pageLocation: this.pageLocation,
                    url: location.href,
                    recaptchaToken: token,
                };

                if (this.botVerified) {
                    data.botChallenge = this.botKey;
                }
                
                let response = await Api.post('leads/start-conversation', data);

                if ((response.valid) && (response.results) && (!response.results.error)) {

                    Analytics.event('PhoneCta', {
                        type: this.type,
                        page_location: this.pageLocation,
                    });

                    let eventData = {
                        id: response.results.id,
                        token: response.results.token,
                    };

                    this.$emit('submit', eventData);
                    
                    if ((this.followUp) && (response.results.id) && (response.results.token)) {
                        this.ctaId = response.results.id;
                        this.ctaToken = response.results.token;
                        this.openEmailModal();
                    }
                    // if no follow up, we'll let the parent compenent handle everything
                    else if (this.followUp) {
                        this.uiStore.toast({
                            success: true,
                            title: 'Thank You',
                            message: 'We will reach out shortly',
                        });
                    }
                    
                    this.startValidation = false;
                    this.localNumber = null;
                    this.localNumberValid = null;

                }
                else {
                    let error = 'Could not complete request';
                    if ((response.results) && (response.results.error)) {

                        if (response.results.error.message) {
                            error = response.results.error.message;
                        }
                        else if (response.results.error.botChallenge) {
                            error = null;
                            this.botVerified = false;
                            this.botKey = response.results.error.botChallenge;
                            this.showChallenge = true;
                        }
                        else {
                            error = response.results.error;
                        }
                    }

                    if (error) {
                    
                        this.uiStore.toast({
                            error: true,
                            title: 'Error',
                            message: error,
                        });
                    }
                }

                this.buttonsLoading = false;

            },

            verifyHuman() {
                this.botVerified = true;

                this.processForm();
            },

            openEmailModal() {
                this.emailModalOpen = true;
                this.showEmailModal = true;
            },

            closeEmailModal() {
                this.showEmailModal = false;
                this.emailModalOpen = true;
                this.showThankYou = false;
            },

            validateFollowUp() {
                if (!this.startValidation2) return;

                let valid = true;
                this.firstNameValid = null;
                this.lastNameValid = null;
                this.emailValid = null;
                this.hasBookingValid = null;

                if (this.address) {
                    this.address.resetValidation();
                }

                if (!this.firstName) {
                    valid = false;
                    this.firstNameValid = false;
                }
                if (!this.lastName) {
                    valid = false;
                    this.lastNameValid = false;
                }
                if (!this.email) {
                    valid = false;
                    this.emailValid = false;
                }

                if ((this.showAddress) && (this.address) && (!this.address.validate())) {
                    valid = false;
                    this.addressValid = false;
                }

                if ((this.showHasBooking) && (this.hasBooking == null)) {
                    valid = false;
                    this.hasBookingValid = false;
                }

                return valid;
            },

            async processFollowUpForm() {
                this.startValidation2 = true;

                if (!this.validateFollowUp()) {
                    this.uiStore.toast({
                        error: true,
                        title: 'Required Fields',
                        message: 'Please complete the required highlighted fields',
                    });

                    return;
                }

                this.buttonsLoading = true;

                let data = {
                    ctaId: this.ctaId,
                    ctaToken: this.ctaToken,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    address: this.address ? this.address.toJSON() : null,
                };

                if (this.showHasBooking) {
                    data.data = {
                        hasBooking: this.hasBooking,
                    };
                }
                
                let response = await Api.post('leads/start-conversation-followup', data);

                if ((response.valid) && (response.results) && (!response.results.error)) {

                    Analytics.event('PhoneCtaFollowup');
                    
                    this.showThankYou = true;
                    
                    this.startValidation2 = false;
                    this.firstNameValid = null;
                    this.lastNameValid = null;
                    this.emailValid = null;

                    this.$emit('followup');

                }
                else {
                    let error = 'Could not complete request';
                    if ((response.results) && (response.results.error)) {

                        if (response.results.error.message) {
                            error = response.results.error.message;
                        }
                        else {
                            error = response.results.error;
                        }
                    }

                    this.uiStore.toast({
                        error: true,
                        title: 'Error',
                        message: error,
                    });
                }

                this.buttonsLoading = false;
            }

        },
    };

</script>
